import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SessionDataResult } from '../../../interfaces/services';
import { getServicesManager } from '../../../services';
import { RootState } from '../../../store';
import { 
  startLoading, 
  loadSuccess, 
  loadError,
  stopLoading,
} from './data-slice';
import { SEC_TO_MS } from '../../../cfg/const';

interface UseSessionDetailsProps {
  sessionId?: string;
}

interface SessionDetailsHookResult {
  sessionData: SessionDataResult | undefined;
  isLoading: boolean;
  error: string | undefined;
  hasData: (sessionId: string | undefined) => boolean;
  refresh: () => void;
}

export const useSessionDetails = ({
  sessionId,
}: UseSessionDetailsProps): SessionDetailsHookResult => {
  const dispatch = useDispatch();
  const sessionDetails = useSelector((state: RootState) => 
    sessionId ? state.sessionDetails.sessionIdToDetails[sessionId] : undefined
  );

  // Track if we have data for the current sessionId
  const hasData = useCallback((sid: string | undefined) => {
    if (!sid) return false;
    return !!sessionDetails?.data && sessionDetails.data.session.session_id === sid;
  }, [sessionDetails]);

  const fetchSessionData = useCallback(async (sessionIdToFetch: string, attempt: number = 0) => {
    if (!sessionIdToFetch) return;
    if (hasData(sessionIdToFetch)) return;
    if (sessionDetails?.isLoading && attempt === 0) return;
    if (attempt > 3) {
      dispatch(loadError({ sessionId: sessionIdToFetch, error: 'Failed to fetch session data' }));
      return;
    }
    
    dispatch(startLoading(sessionIdToFetch));
    
    try {
      const data = await getServicesManager().getSessionData(sessionIdToFetch);
      // if it's a number, we need to back off and try again
      if (data && typeof data !== 'number' && data.session.session_id === sessionIdToFetch) {
        dispatch(loadSuccess({ sessionId: sessionIdToFetch, data }));
      } else if (data && typeof data === 'number') {
        // if it's a number, we need to back off and try again
        setTimeout(() => {
          if (sessionIdToFetch === sessionId) {
            fetchSessionData(sessionIdToFetch, attempt + 1);
          } else {
            dispatch(stopLoading(sessionIdToFetch));
          }
        }, 2*SEC_TO_MS);
      } else {
        dispatch(loadError({ sessionId: sessionIdToFetch, error: 'Failed to fetch session data' }));
      }
    } catch (error) {
      dispatch(loadError({
        sessionId: sessionIdToFetch,
        error: error instanceof Error ? error.message : 'An error occurred'
      }));
    } 
  }, [sessionId, dispatch, sessionDetails, hasData]);

  const refresh = useCallback(() => {
    if (sessionId) {
      fetchSessionData(sessionId, 0);
    }
  }, [sessionId, fetchSessionData]);

  useEffect(() => {
    if (sessionId && !sessionDetails) {
        fetchSessionData(sessionId);
    }
  }, [sessionId, sessionDetails, fetchSessionData]);

  return {
    sessionData: sessionDetails?.data ?? undefined,
    isLoading: sessionDetails?.isLoading ?? false,
    error: sessionDetails?.error ?? undefined,
    hasData,
    refresh
  };
};
